/**
 * Push Notifications Hook
 * - Registers service worker
 * - Subscribes to push notifications
 * - Manages subscription state
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [loading, setLoading] = useState(false);
  const swRef = useRef<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    if (supported) {
      setPermission(Notification.permission);
      registerSW();
    }
  }, []);

  const registerSW = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      swRef.current = reg;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch (e) {
      console.error('[Push] SW registration failed:', e);
    }
  }, []);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported || !swRef.current) return false;
    setLoading(true);
    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setLoading(false);
        return false;
      }

      // Get VAPID key
      const { data } = await api.get('/notifications/vapid-key');
      const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

      // Subscribe with push manager
      const sub = await swRef.current.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource,
      });

      // Send subscription to backend
      const subJson = sub.toJSON();
      await api.post('/notifications/subscribe', {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      setIsSubscribed(true);
      setLoading(false);
      return true;
    } catch (e) {
      console.error('[Push] Subscribe failed:', e);
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!swRef.current) return false;
    setLoading(true);
    try {
      const sub = await swRef.current.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await api.post('/notifications/unsubscribe', null, { params: { endpoint } });
      }
      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (e) {
      console.error('[Push] Unsubscribe failed:', e);
      setLoading(false);
      return false;
    }
  }, []);

  return { isSupported, isSubscribed, permission, loading, subscribe, unsubscribe };
}
